(function () {
  'use strict';

  var reducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    initFadeIn();
    initLogoStamp();
    initLinkUnderlines();
    initHeaderScroll();
    initCountdowns();
    initCartToast();
    initCartPage();
    initMobileMenu();
    initCartCountSync();
    initCartDrawer();
    initAddToCartForms();
    initSearchModal();
    initRewardBanner();
    initClueHunt();
  }

  /* Search modal — opens from any [data-vf-search-open] trigger. */
  function initSearchModal() {
    var modal = document.querySelector('[data-vf-search-modal]');
    if (!modal) return;
    var input = modal.querySelector('[data-vf-search-input]');

    function setOpen(open) {
      document.body.classList.toggle('vf-search-open', open);
      modal.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open) {
        modal.removeAttribute('inert');
        if (input) setTimeout(function () { input.focus(); }, 80);
      } else {
        modal.setAttribute('inert', '');
      }
    }
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-vf-search-open]')) { e.preventDefault(); setOpen(true); }
      if (e.target.closest('[data-vf-search-close]')) { e.preventDefault(); setOpen(false); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.body.classList.contains('vf-search-open')) setOpen(false);
    });
  }

  /* Persistent reward banner after the 404 game unlock.
     The 404 JS sets localStorage 'tvf_404_reward_until' on success.
     We surface a small bottom-of-page banner with the code on every
     page until the cooldown expires, so users don't lose THEVF404. */
  function initRewardBanner() {
    var STORE = 'tvf_404_reward_until';
    var DISMISS = 'vf_reward_dismissed_until';
    var CODE = 'THEVF404';

    function rawNum(key) {
      try { var v = localStorage.getItem(key); return v ? parseInt(v, 10) : 0; }
      catch (e) { return 0; }
    }
    var until = rawNum(STORE);
    var dismissed = rawNum(DISMISS);
    var now = Date.now();
    if (!until || until <= now) return;
    if (dismissed && dismissed > now) return;

    var banner = document.createElement('aside');
    banner.className = 'vf-reward-banner';
    banner.setAttribute('role', 'status');
    banner.innerHTML =
      '<div class="vf-reward-banner__inner">' +
        '<span class="vf-reward-banner__eyebrow">Your Reward</span>' +
        '<span class="vf-reward-banner__body">5% off your next case file. Code: <button type="button" class="vf-reward-banner__code" data-vf-reward-copy>' + CODE + '</button></span>' +
        '<button type="button" class="vf-reward-banner__close" data-vf-reward-close aria-label="Dismiss">&times;</button>' +
      '</div>';
    document.body.appendChild(banner);

    banner.querySelector('[data-vf-reward-copy]').addEventListener('click', function (e) {
      var t = e.currentTarget;
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(CODE).then(function () {
        var orig = t.textContent;
        t.textContent = 'Copied';
        setTimeout(function () { t.textContent = orig; }, 1200);
      });
    });
    banner.querySelector('[data-vf-reward-close]').addEventListener('click', function () {
      try { localStorage.setItem(DISMISS, String(Date.now() + 6 * 60 * 60 * 1000)); } catch (e) {}
      banner.remove();
    });
  }

  /* Intercept every Add-to-Cart form submit so it AJAX-adds via
     /cart/add.js instead of doing a full navigation to /cart.
     On success we dispatch vf:cart:added — the toast and drawer
     listen for that and respond (drawer opens automatically). */
  function initAddToCartForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.tagName !== 'FORM') return;
      var action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;

      e.preventDefault();

      var btn = form.querySelector('[type="submit"]');
      var originalLabel = '';
      if (btn) {
        originalLabel = btn.textContent;
        btn.disabled = true;
        btn.dataset.vfBusy = '1';
        btn.textContent = 'Adding…';
      }

      var data = new FormData(form);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: data
      }).then(function (r) {
        if (!r.ok) {
          return r.json().then(function (err) { throw err; });
        }
        return r.json();
      }).then(function (line) {
        document.dispatchEvent(new CustomEvent('vf:cart:added', { detail: line }));
        if (btn) {
          btn.textContent = 'Added';
          setTimeout(function () {
            btn.disabled = false;
            btn.textContent = originalLabel || 'Add to Bag';
            delete btn.dataset.vfBusy;
          }, 800);
        }
      }).catch(function (err) {
        if (btn) {
          btn.disabled = false;
          btn.textContent = originalLabel || 'Add to Bag';
          delete btn.dataset.vfBusy;
        }
        var msg = (err && err.description) || (err && err.message) || 'Could not add. Try again.';
        // Surface a quick inline status if the form has one
        var status = form.querySelector('[aria-live="polite"]');
        if (status) status.textContent = msg;
      });
    });
  }

  /* Cart side drawer — slides in from the right when the header bag
     icon is clicked. Open/close handlers plus qty +/- and remove
     are wired here. After any cart mutation (add/change/update/clear)
     anywhere on the page we refetch the drawer's section markup via
     Shopify's Section Rendering API and swap the panel's innerHTML,
     so the drawer always shows the current cart without a full reload. */
  function initCartDrawer() {
    var drawer = document.querySelector('[data-vf-drawer]');
    if (!drawer) return;

    var SECTION_ID = 'vf-cart-drawer';

    function vfCssEscape(s) {
      if (window.CSS && CSS.escape) return CSS.escape(s);
      return String(s).replace(/[^a-zA-Z0-9_-]/g, function (c) { return '\\' + c; });
    }

    function setOpen(open) {
      document.body.classList.toggle('vf-drawer-open', open);
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      if (open) {
        drawer.removeAttribute('inert');
        // Move focus to the close button for keyboard a11y
        var closeBtn = drawer.querySelector('[data-vf-drawer-close]');
        if (closeBtn) setTimeout(function () { closeBtn.focus({ preventScroll: true }); }, 50);
      } else {
        drawer.setAttribute('inert', '');
      }
    }

    function open()  { setOpen(true);  }
    function close() { setOpen(false); }

    // Header bag icon (and any other element marked data-vf-drawer-open)
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-vf-drawer-open]');
      if (!opener) return;
      e.preventDefault();
      open();
    });

    // Close handlers (close button + scrim)
    drawer.addEventListener('click', function (e) {
      if (e.target.closest('[data-vf-drawer-close]')) {
        e.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.body.classList.contains('vf-drawer-open')) {
        close();
      }
    });

    // Qty + remove inside the drawer
    var pending = {};
    var sendTimer = null;
    var inflight = false;

    function flush() {
      sendTimer = null;
      if (inflight) return;
      var keys = Object.keys(pending);
      if (!keys.length) return;
      var batch = pending;
      pending = {};
      inflight = true;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates: batch })
      }).then(function () { inflight = false; refreshDrawer(); })
        .catch(function () { inflight = false; refreshDrawer(); });
    }

    function queueUpdate(key, qty) {
      if (!key) return;
      pending[key] = qty;
      // optimistic paint
      drawer.querySelectorAll('[data-vf-drawer-qty][data-key="' + vfCssEscape(key) + '"]').forEach(function (el) {
        el.value = String(qty);
      });
      if (qty === 0) {
        var row = drawer.querySelector('[data-vf-drawer-item][data-key="' + vfCssEscape(key) + '"]');
        if (row) row.classList.add('is-removing');
      }
      if (sendTimer) clearTimeout(sendTimer);
      sendTimer = setTimeout(flush, 220);
    }

    drawer.addEventListener('click', function (e) {
      var rem = e.target.closest('[data-vf-drawer-remove]');
      if (rem) {
        e.preventDefault();
        e.stopPropagation();
        queueUpdate(rem.getAttribute('data-key'), 0);
        return;
      }
      var btn = e.target.closest('[data-vf-drawer-decr], [data-vf-drawer-incr]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var key = btn.getAttribute('data-key');
      var anyInput = drawer.querySelector('[data-vf-drawer-qty][data-key="' + vfCssEscape(key) + '"]');
      var current = anyInput ? (parseInt(anyInput.value, 10) || 0) : 0;
      var dir = btn.hasAttribute('data-vf-drawer-incr') ? 1 : -1;
      var next = Math.max(0, current + dir);
      queueUpdate(key, next);
    });

    /* Section refresh — pulls fresh drawer HTML via Shopify's
       Section Rendering API and swaps inner content while keeping
       the wrapper (and its open state). */
    var refreshing = false;
    function refreshDrawer() {
      if (refreshing) return Promise.resolve();
      refreshing = true;
      document.body.classList.add('vf-drawer-refreshing');
      return fetch('/?sections=' + SECTION_ID, {
        headers: { 'Accept': 'application/json' }
      }).then(function (r) { return r.json(); })
        .then(function (data) {
          var html = data && data[SECTION_ID];
          if (!html) return;
          var tmp = document.createElement('div');
          tmp.innerHTML = html;
          var fresh = tmp.querySelector('[data-vf-drawer]');
          if (!fresh) return;
          drawer.innerHTML = fresh.innerHTML;
        })
        .catch(function () {})
        .then(function () {
          refreshing = false;
          document.body.classList.remove('vf-drawer-refreshing');
        });
    }

    // Watch every cart mutation initiated anywhere on the page
    // and refresh the drawer right after.
    var nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isMutation =
        url.indexOf('/cart/add') !== -1 ||
        url.indexOf('/cart/change') !== -1 ||
        url.indexOf('/cart/update') !== -1 ||
        url.indexOf('/cart/clear') !== -1;
      var p = nativeFetch(input, init);
      if (!isMutation) return p;
      return p.then(function (res) {
        if (res && res.ok) setTimeout(refreshDrawer, 80);
        return res;
      });
    };

    // Auto-open the drawer right after a successful add.
    // initAddToCartForms dispatches this event.
    document.addEventListener('vf:cart:added', function () {
      // Wait for the section to refresh so the new item is in view
      setTimeout(open, 220);
    });

  }

  /* Hidden Clue Hunt → /404
     Three diamond marks scattered across the site carry
     [data-vf-clue="1|2|3"]. After each find, the toast tells the
     user how many remain AND points to the pages where the missing
     fragments live. When all three are found, a finale toast plays
     and we redirect to /404. */
  function initClueHunt() {
    var STORE = 'vf_clues_found_v1';
    var clueEls = document.querySelectorAll('[data-vf-clue]');
    if (!clueEls.length) return;

    // Riddles, not directions. Each one points at the page where
    // its fragment is hidden without naming it — the player has to
    // figure out the location from the clue.
    var CLUE_RIDDLES = {
      '1': 'Where the truth first met the details.',
      '2': 'Where the studio tells its own story.',
      '3': 'In the fine print, where we admit that none of it is real.'
    };
    var CLUE_NAMES = {
      '1': 'First fragment.',
      '2': 'Second fragment.',
      '3': 'Third fragment.'
    };

    function readFound() {
      try {
        var raw = localStorage.getItem(STORE);
        if (!raw) return [];
        var arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr : [];
      } catch (e) { return []; }
    }
    function saveFound(arr) {
      try { localStorage.setItem(STORE, JSON.stringify(arr)); } catch (e) {}
    }

    function paintFound(found) {
      clueEls.forEach(function (el) {
        var id = el.getAttribute('data-vf-clue');
        if (found.indexOf(id) !== -1) el.classList.add('vf-clue--found');
        else el.classList.remove('vf-clue--found');
      });
    }

    function buildToast() {
      var existing = document.querySelector('[data-vf-clue-toast]');
      if (existing) return existing;
      var t = document.createElement('div');
      t.className = 'vf-clue-toast';
      t.setAttribute('data-vf-clue-toast', '');
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      t.hidden = true;
      t.innerHTML =
        '<div class="vf-clue-toast__inner">' +
          '<span class="vf-clue-toast__mark" aria-hidden="true">&#9670;</span>' +
          '<div class="vf-clue-toast__body">' +
            '<span class="vf-clue-toast__label" data-clue-label></span>' +
            '<span class="vf-clue-toast__hint" data-clue-hint></span>' +
            '<span class="vf-clue-toast__count" data-clue-count></span>' +
          '</div>' +
          '<button type="button" class="vf-clue-toast__close" data-clue-close aria-label="Dismiss">&times;</button>' +
        '</div>';
      document.body.appendChild(t);
      t.querySelector('[data-clue-close]').addEventListener('click', function () {
        hideToast(t);
      });
      return t;
    }

    function hideToast(t) {
      t.removeAttribute('data-open');
      clearTimeout(showToast._t);
      setTimeout(function () { t.hidden = true; }, 400);
    }

    function showToast(label, hint, count, finale) {
      var t = buildToast();
      t.querySelector('[data-clue-label]').textContent = label;
      t.querySelector('[data-clue-hint]').textContent = hint || '';
      t.querySelector('[data-clue-count]').textContent = count || '';
      t.hidden = false;
      if (finale) t.setAttribute('data-finale', '');
      else t.removeAttribute('data-finale');
      void t.offsetWidth;
      t.setAttribute('data-open', '');
      clearTimeout(showToast._t);
      showToast._t = setTimeout(function () { hideToast(t); }, finale ? 2400 : 7500);
    }

    function buildHint(found) {
      var allIds = ['1', '2', '3'];
      var missing = allIds.filter(function (id) { return found.indexOf(id) === -1; });
      if (!missing.length) return '';
      var riddles = missing.map(function (id) { return CLUE_RIDDLES[id]; });
      if (riddles.length === 1) {
        return 'One fragment remains. ' + riddles[0];
      }
      if (riddles.length === 2) {
        return 'Two fragments remain. ' + riddles[0] + ' ' + riddles[1];
      }
      return 'Three fragments remain. ' + riddles.join(' ');
    }

    function activate(el) {
      var id = el.getAttribute('data-vf-clue');
      var found = readFound();

      if (found.indexOf(id) !== -1) {
        showToast(
          'You have already found this fragment.',
          buildHint(found),
          'Progress: ' + found.length + ' of 3',
          false
        );
        return;
      }

      found.push(id);
      saveFound(found);
      paintFound(found);

      if (found.length >= 3) {
        showToast(
          'All three fragments collected.',
          'The case opens. You are being taken to the page that was hidden.',
          'Reward inside.',
          true
        );
        setTimeout(function () { window.location.href = '/404'; }, 2400);
        return;
      }

      var label = CLUE_NAMES[id] + ' A fragment of something hidden.';
      var hint = buildHint(found);
      var countText = 'Found ' + found.length + ' of 3.';
      showToast(label, hint, countText, false);
    }

    paintFound(readFound());
    clueEls.forEach(function (el) {
      el.classList.add('vf-clue');
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        activate(el);
      });
      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate(el);
        }
      });
    });
  }

  /* Mobile menu — hamburger toggles body class .vf-menu-open */
  function initMobileMenu() {
    var toggle = document.querySelector('[data-vf-menu-toggle]');
    var menu = document.querySelector('[data-vf-mobile-menu]');
    if (!toggle || !menu) return;
    var closers = menu.querySelectorAll('[data-vf-menu-close]');

    function open() {
      document.body.classList.add('vf-menu-open');
      toggle.setAttribute('aria-expanded', 'true');
      menu.removeAttribute('inert');
      menu.setAttribute('aria-hidden', 'false');
    }
    function close() {
      document.body.classList.remove('vf-menu-open');
      toggle.setAttribute('aria-expanded', 'false');
      menu.setAttribute('aria-hidden', 'true');
      menu.setAttribute('inert', '');
    }

    toggle.addEventListener('click', function () {
      if (document.body.classList.contains('vf-menu-open')) close();
      else open();
    });
    closers.forEach(function (el) { el.addEventListener('click', close); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && document.body.classList.contains('vf-menu-open')) close();
    });
    // Close when navigating to an internal anchor
    menu.querySelectorAll('a[href]').forEach(function (a) {
      a.addEventListener('click', close);
    });
  }

  /* Keep [data-vf-cart-count] in sync after add/change. Reads
     /cart.js after any successful /cart/add or /cart/change. */
  function initCartCountSync() {
    var els = document.querySelectorAll('[data-vf-cart-count]');
    if (!els.length || !window.fetch) return;

    function paint(n) {
      els.forEach(function (el) {
        el.textContent = String(n);
        if (n > 0) el.classList.remove('is-empty');
        else el.classList.add('is-empty');
      });
    }

    function refresh() {
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { if (cart && typeof cart.item_count === 'number') paint(cart.item_count); })
        .catch(function () {});
    }

    // Refresh after any cart mutation
    var nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isMutation = url.indexOf('/cart/add') !== -1
                    || url.indexOf('/cart/change') !== -1
                    || url.indexOf('/cart/update') !== -1
                    || url.indexOf('/cart/clear') !== -1;
      var p = nativeFetch(input, init);
      if (!isMutation) return p;
      return p.then(function (res) {
        if (res && res.ok) setTimeout(refresh, 50);
        return res;
      });
    };
  }

  /* Cart page line-item controls.
     - Reads the line key directly off the button (data-key) so we
       don't depend on event bubbling through ancestor elements.
     - Updates EVERY input matching that key (mobile + desktop copies)
       optimistically before the round-trip.
     - Debounces the network call slightly so rapid clicks coalesce. */
  function initCartPage() {
    var root = document.querySelector('[data-vf-cart]');
    if (!root) return;

    var pending = {};   // key → quantity
    var sendTimer = null;
    var inflight = false;

    function paintQty(key, qty) {
      root.querySelectorAll('[data-vf-cart-qty][data-key="' + cssEscape(key) + '"]').forEach(function (el) {
        el.value = String(qty);
      });
    }

    function cssEscape(s) {
      if (window.CSS && CSS.escape) return CSS.escape(s);
      return String(s).replace(/[^a-zA-Z0-9_-]/g, function (c) {
        return '\\' + c;
      });
    }

    function flush() {
      sendTimer = null;
      if (inflight) return;
      var keys = Object.keys(pending);
      if (!keys.length) return;
      var batch = pending;
      pending = {};
      inflight = true;

      // /cart/update.js accepts an updates map keyed by line-item key
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ updates: batch })
      }).then(function () {
        inflight = false;
        window.location.reload();
      }).catch(function () {
        inflight = false;
        window.location.reload();
      });
    }

    function queueUpdate(key, qty) {
      if (!key) return;
      pending[key] = qty;
      paintQty(key, qty);
      if (sendTimer) clearTimeout(sendTimer);
      sendTimer = setTimeout(flush, 220);
    }

    root.addEventListener('click', function (e) {
      var rem = e.target.closest('[data-vf-cart-remove]');
      if (rem) {
        e.preventDefault();
        e.stopPropagation();
        queueUpdate(rem.getAttribute('data-key'), 0);
        // Visually fade the line right away
        var row = rem.closest('[data-vf-cart-item]');
        if (row) row.style.opacity = '0.4';
        return;
      }

      var btn = e.target.closest('[data-vf-cart-decr], [data-vf-cart-incr]');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      var key = btn.getAttribute('data-key');
      if (!key) return;

      var current = 0;
      var anyInput = root.querySelector('[data-vf-cart-qty][data-key="' + cssEscape(key) + '"]');
      if (anyInput) current = parseInt(anyInput.value, 10) || 0;

      var dir = btn.hasAttribute('data-vf-cart-incr') ? 1 : -1;
      var next = Math.max(0, current + dir);
      queueUpdate(key, next);
    });
  }

  /* Added-to-bag toast. Wraps window.fetch and listens for successful
     POSTs to /cart/add.js (any URL containing "/cart/add"). On 2xx the
     toast surfaces with the product title + image pulled from the JSON
     response. Auto-dismisses after ~4s. Coexists with the Savor cart
     drawer — we don't open it, we just confirm. */
  function initCartToast() {
    var toast = document.querySelector('[data-vf-cart-toast]');
    if (!toast) return;
    if (!window.fetch) return;

    var titleEl = toast.querySelector('[data-vf-toast-title]');
    var imgEl   = toast.querySelector('[data-vf-toast-img]');
    var dismissEls = toast.querySelectorAll('[data-vf-toast-dismiss]');
    var hideTimer = null;

    function show(payload) {
      if (!payload) return;

      var name = payload.product_title || payload.title || 'Case file';
      // Strip Shopify variant suffix " - Default Title"
      name = String(name).replace(/ - Default Title$/i, '');
      titleEl.textContent = name;

      var imgSrc = payload.featured_image && (payload.featured_image.url || payload.featured_image);
      imgSrc = imgSrc || payload.image || payload.featured_media_url || '';
      if (imgSrc) {
        var sized = String(imgSrc).indexOf('?') === -1
          ? imgSrc + '?width=240'
          : imgSrc;
        imgEl.setAttribute('src', sized);
        imgEl.setAttribute('alt', name);
      } else {
        imgEl.removeAttribute('src');
      }

      toast.hidden = false;
      // Force reflow so the open-state transition fires
      void toast.offsetWidth;
      toast.setAttribute('data-vf-toast-state', 'open');

      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(hide, 4500);
    }

    function hide() {
      toast.removeAttribute('data-vf-toast-state');
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
      setTimeout(function () {
        if (!toast.hasAttribute('data-vf-toast-state')) toast.hidden = true;
      }, 350);
    }

    dismissEls.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        hide();
      });
    });

    // Pause auto-dismiss on hover/focus so users can read the toast
    toast.addEventListener('mouseenter', function () {
      if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
    });
    toast.addEventListener('mouseleave', function () {
      hideTimer = setTimeout(hide, 2500);
    });

    var nativeFetch = window.fetch.bind(window);
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isAdd = url.indexOf('/cart/add') !== -1;
      var promise = nativeFetch(input, init);
      if (!isAdd) return promise;

      return promise.then(function (res) {
        if (!res || !res.ok) return res;
        // Clone so callers can still read the body
        res.clone().json().then(function (data) {
          if (!data) return;
          // /cart/add.js returns the single line item.
          // Some clients send {items:[...]} (cart/update). Handle both.
          var payload = data;
          if (data.items && data.items.length) {
            payload = data.items[data.items.length - 1];
          }
          show(payload);
        }).catch(function () { /* not JSON or already consumed */ });
        return res;
      });
    };

    // Also listen for a manual event in case any code path bypasses fetch
    document.addEventListener('vf:cart:added', function (e) {
      show(e.detail || {});
    });
  }

  /* Countdown timer (snippets/vf-countdown.liquid).
     Targets every [data-vf-countdown] element on the page,
     reads its data-target ISO date, and updates Days/Hours/Min
     every 30s. After the target passes, swaps to the complete
     message in [data-vf-cd-done]. */
  function initCountdowns() {
    var nodes = document.querySelectorAll('[data-vf-countdown]');
    if (!nodes.length) return;

    function pad(n) { return n < 10 ? '0' + n : String(n); }

    function update(el) {
      var target = new Date(el.getAttribute('data-target')).getTime();
      if (isNaN(target)) return;
      var now = Date.now();
      var diff = target - now;
      var doneEl = el.querySelector('[data-vf-cd-done]');
      var figuresEl = el.querySelector('[data-vf-cd-figures]');

      if (diff <= 0) {
        if (figuresEl) figuresEl.style.display = 'none';
        if (doneEl) doneEl.hidden = false;
        el.setAttribute('data-vf-cd-state', 'complete');
        return;
      }

      var days  = Math.floor(diff / (1000 * 60 * 60 * 24));
      var hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      var mins  = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

      var d = el.querySelector('[data-vf-cd-days]');
      var h = el.querySelector('[data-vf-cd-hours]');
      var m = el.querySelector('[data-vf-cd-mins]');
      if (d) d.textContent = pad(days);
      if (h) h.textContent = pad(hours);
      if (m) m.textContent = pad(mins);
    }

    function tick() { nodes.forEach(update); }

    tick();
    setInterval(tick, 30000);
  }

  /* Fade-in on scroll for sections inside vf-* areas.
     Progressive enhancement: content is visible by default (the fade
     class is only added by JS). A scroll + timeout failsafe reveals
     anything a fast jump (scrollbar drag, find-in-page, anchor link)
     skips past the observer, so content is never left stuck hidden. */
  function initFadeIn() {
    if (reducedMotion) return;
    if (!('IntersectionObserver' in window)) return;

    var targets = document.querySelectorAll(
      [
        '.vf-section',
        '.vf-hero__inner',
        '.vf-hiw__header',
        '.vf-hiw .vf-step',
        '.vf-om-grid__header',
        '.vf-om-card',
        '.vf-story__media',
        '.vf-story__copy',
        '.vf-about__main',
        '.vf-footer__newsletter',
        '.vf-footer__grid'
      ].join(',')
    );

    if (!targets.length) return;

    function reveal(el) {
      if (el.classList.contains('is-in')) return;
      el.classList.add('is-in');
      io.unobserve(el);
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) reveal(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );

    targets.forEach(function (el, i) {
      el.classList.add('vf-fade');
      el.style.transitionDelay = Math.min(i * 80, 480) + 'ms';
      io.observe(el);
    });

    // Failsafe: reveal anything at or above the viewport that a fast
    // jump may have skipped. Throttled on scroll, plus hashchange and a
    // one-shot timer so nothing can stay stuck at opacity 0.
    var pending = false;
    function sweep() {
      pending = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      targets.forEach(function (el) {
        if (el.classList.contains('is-in')) return;
        if (el.getBoundingClientRect().top < vh * 0.95) reveal(el);
      });
    }
    function onScroll() {
      if (pending) return;
      pending = true;
      requestAnimationFrame(sweep);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('hashchange', function () { setTimeout(sweep, 60); });
    setTimeout(sweep, 4000);
  }

  /* Header compacts on scroll: adds .vf-scrolled to <body> when scrolled.
     Also publishes --vf-header-height so other CSS (cart drawer scroll-hint,
     sticky offsets) can use it without depending on Savor's web-component. */
  function initHeaderScroll() {
    var threshold = 24;
    var raf = null;
    var header = document.querySelector('[data-vf-header]');

    function publishHeight() {
      if (!header) return;
      var h = header.offsetHeight;
      document.body.style.setProperty('--vf-header-height', h + 'px');
      document.body.style.setProperty('--header-height', h + 'px');
    }

    function update() {
      raf = null;
      if (window.scrollY > threshold) document.body.classList.add('vf-scrolled');
      else document.body.classList.remove('vf-scrolled');
    }
    window.addEventListener('scroll', function () {
      if (raf !== null) return;
      raf = window.requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', publishHeight);
    update();
    publishHeight();
  }

  /* Apply stamp animation to the brand logo on first paint */
  function initLogoStamp() {
    var candidates = document.querySelectorAll(
      '.vf-footer__logo img, .header__logo img, .header__heading-logo, .logo img, [data-vf-logo]'
    );
    candidates.forEach(function (el) {
      el.classList.add('vf-logo-stamp');
    });
  }

  /* Apply hairline underline animation class to text links inside prose */
  function initLinkUnderlines() {
    var prose = document.querySelectorAll(
      '.rte, .vf-richtext, .vf-about__content, .vf-om-header__desc'
    );
    prose.forEach(function (root) {
      var anchors = root.querySelectorAll('a:not(.vf-btn):not(.vf-om-btn)');
      anchors.forEach(function (a) {
        a.classList.add('vf-link');
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', init);
})();
